/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * EngagementHeatmapView — precinct (rows) × period (columns) grid of knock
 * and conversation metrics, colored by engagement_score.
 *
 * This is the orchestrator. Everything interesting (fetching, rendering,
 * drill-down) lives in `src/components/heatmap/`. The view only wires
 * state + dispatches to child components.
 *
 * Decomposition (session 5.12, 2026-04-16):
 *   - useEngagementHeatmap → data fetch with cancellation flag.
 *   - useHeatmapCellDetail → drill-down data fetch, independent cancel.
 *   - HeatmapFilters → filter strip.
 *   - HeatmapLegend → color scale.
 *   - HeatmapGrid → sticky-header table + clickable cells.
 *   - HeatmapLoadingSkeleton / HeatmapEmptyState → non-happy-path cards.
 *   - HeatmapDetailDrawer → side drawer with paginated knock list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import type { HeatmapCell } from '@/api/narrative';
import client from '@/api/client';
import {
  OnboardingModal,
  OnboardingHelpButton,
} from '@/components/OnboardingModal';
import {
  useEngagementHeatmap,
  HeatmapFilters,
  HeatmapGrid,
  HeatmapLegend,
  HeatmapLoadingSkeleton,
  HeatmapEmptyState,
  HeatmapDetailDrawer,
  type HeatmapFilterState,
} from '@/components/heatmap';

interface PrivacyMeta {
  min_knock_threshold?: number;
}

export function EngagementHeatmapView({
  onBack: _onBack,
  onDrill: _onDrill,
}: {
  onBack?: () => void;
  // Preserve the contextual drill-up prop for future wiring, even though
  // the heatmap's own drill-down goes through the side drawer.
  onDrill?: (target: string, value: any) => void;
}) {
  const [privacyFloor, setPrivacyFloor] = useState<number>(5);
  const [filters, setFilters] = useState<HeatmapFilterState>({
    sinceDays: 45,
    bucket: 'weekly',
    minN: 5,
  });
  const [selectedCell, setSelectedCell] = useState<HeatmapCell | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);

  // Fetch the tenant's privacy floor once on mount so the min-N control
  // can't drop below it. Silent-fail to the default (5) if the meta
  // endpoint is unavailable.
  useEffect(() => {
    let cancelled = false;
    client
      .get<PrivacyMeta>('/meta/privacy')
      .then((res) => {
        if (cancelled) return;
        const floor = res.data?.min_knock_threshold ?? 5;
        setPrivacyFloor(floor);
        setFilters((prev) => ({
          ...prev,
          minN: Math.max(prev.minN, floor),
        }));
      })
      .catch(() => {
        if (!cancelled) setPrivacyFloor(5);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data, loading, error, refetch } = useEngagementHeatmap({
    sinceDays: filters.sinceDays,
    bucket: filters.bucket,
    minN: filters.minN,
  });

  const resetFilters = useCallback(() => {
    setFilters({ sinceDays: 45, bucket: 'weekly', minN: privacyFloor });
  }, [privacyFloor]);

  const selectedKey = selectedCell
    ? `${selectedCell.precinct_num}|${selectedCell.period_start}`
    : null;

  const header = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-fg-primary">Engagement Heatmap</h1>
          <OnboardingHelpButton
            onClick={() => setForceOnboard(true)}
            label="About"
          />
        </div>
      </div>

      <p className="text-fg-secondary text-sm max-w-3xl">
        Knock-to-conversation conversion × precinct × time. Color intensity
        reflects engagement (darker = lower, brighter green = higher). Click
        any cell to see the underlying knockbacks.
      </p>

      <div
        className="bg-status-warning-bg/20 border border-status-warning-bg/40 rounded-lg p-3"
        data-testid="heatmap-privacy-banner"
      >
        <p className="text-status-warning text-xs">
          Cells with fewer than {privacyFloor} knocks are hidden to protect
          individual privacy.
        </p>
      </div>

      <HeatmapFilters
        value={filters}
        onChange={setFilters}
        privacyFloor={privacyFloor}
      />
    </div>
  );

  // -----------------------------------------------------------------------
  // Loading (initial) — skeleton
  // -----------------------------------------------------------------------
  if (loading && !data) {
    return (
      <div className="space-y-6">
        {header}
        <HeatmapLoadingSkeleton />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Error — retry card
  // -----------------------------------------------------------------------
  if (error) {
    return (
      <div className="space-y-6">
        {header}
        <div
          className="bg-status-danger-bg/20 border border-status-danger-bg/30 rounded-lg p-6"
          data-testid="heatmap-error"
          role="alert"
        >
          <p className="text-status-danger text-sm">{error}</p>
          <button
            type="button"
            onClick={refetch}
            className="mt-4 inline-flex items-center min-h-[44px] px-4 py-2 bg-accent hover:bg-accent-hover rounded text-white text-sm font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Empty
  // -----------------------------------------------------------------------
  if (!data || !data.cells || data.cells.length === 0) {
    return (
      <div className="space-y-6">
        {header}
        <HeatmapEmptyState
          sinceDays={filters.sinceDays}
          minN={filters.minN}
          onReset={resetFilters}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Happy path
  // -----------------------------------------------------------------------
  const totalCells =
    (data.precincts?.length || 0) * (data.periods?.length || 0);
  const rendered = data.cells?.length || 0;
  const suppressed = Math.max(totalCells - rendered, 0);

  return (
    <div className="space-y-6">
      {header}
      <HeatmapLegend />
      <HeatmapGrid
        data={data}
        onSelectCell={setSelectedCell}
        selectedKey={selectedKey}
      />

      {/* Summary footer */}
      <div className="bg-bg-raised rounded-lg border border-border-base p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
          <Stat label="Window start" value={new Date(data.since).toLocaleDateString()} />
          <Stat label="Window end" value={new Date(data.until).toLocaleDateString()} />
          <Stat label="Bucket" value={String(data.bucket)} />
          <Stat label="Cells shown" value={`${rendered}`} />
          <Stat label="Suppressed (K-anon)" value={`${suppressed}`} />
        </div>
      </div>

      <HeatmapDetailDrawer
        cell={selectedCell}
        bucket={filters.bucket}
        onClose={() => setSelectedCell(null)}
      />

      <OnboardingModal
        viewKey="engagement_heatmap"
        title="Engagement Heatmap"
        intro="See how your knock-to-conversation conversion rate varies by precinct and time period. Each cell shows the engagement score — a composite of conversation rate and positive lean. Cells are color-coded from gray (low engagement) to bright green (high engagement). Cells with too few knocks are suppressed for privacy."
        termIds={['engagement_score', 'knock', 'candidate_lean']}
        workflows={[
          'Compare conversion rates across precincts to identify high-performing areas',
          'Spot trends in a single precinct by reading across time periods',
          'Click any cell to see the underlying knockbacks in a side drawer',
          'Use the time bucket toggle to zoom between daily and weekly patterns',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase font-semibold text-fg-muted">
        {label}
      </p>
      <p className="text-sm text-fg-secondary tabular-nums">{value}</p>
    </div>
  );
}

export default EngagementHeatmapView;
